import { FC } from 'react';
import { Card, Table, Tag, Empty, Button, Divider } from 'antd';
import {
  CalendarOutlined,
  HistoryOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import {
  StudentScheduleAdmin,
  LegacySchedule,
  UpcomingScheduleChange,
} from '../../../../../models/admin/student-admin.model';

interface ScheduleTabProps {
  schedules: StudentScheduleAdmin[];
  legacySchedules: LegacySchedule[];
  upcomingChanges: UpcomingScheduleChange[];
  studentId: number;
}

const ScheduleTab: FC<ScheduleTabProps> = ({
  schedules,
  legacySchedules,
  upcomingChanges,
  studentId,
}) => {
  const formatDays = (schedule: StudentScheduleAdmin) => {
    const days = [];
    if (schedule.monday) days.push('M');
    if (schedule.tuesday) days.push('T');
    if (schedule.wednesday) days.push('W');
    if (schedule.thursday) days.push('Th');
    if (schedule.friday) days.push('F');
    return days.join(', ') || 'No days selected';
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const groupSchedulesByProgramType = () => {
    const groups: Record<string, StudentScheduleAdmin[]> = {};
    schedules.forEach((schedule) => {
      const programType =
        schedule.schedule?.program?.programType?.name || 'Other';
      if (!groups[programType]) {
        groups[programType] = [];
      }
      groups[programType].push(schedule);
    });
    return groups;
  };

  const scheduleColumns = [
    {
      title: 'Schedule',
      dataIndex: ['schedule', 'name'],
      key: 'name',
      render: (name: string, record: StudentScheduleAdmin) =>
        record.schedule?.name || `Schedule ${record.scheduleId}`,
    },
    {
      title: 'Days',
      key: 'days',
      render: (_: any, record: StudentScheduleAdmin) => formatDays(record),
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (date: string | null) => formatDate(date),
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (date: string | null) => formatDate(date),
    },
  ];

  const legacyColumns = [
    {
      title: 'Schedule',
      dataIndex: 'scheduleName',
      key: 'scheduleName',
    },
    {
      title: 'Days',
      dataIndex: 'days',
      key: 'days',
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (date: string | null) => formatDate(date),
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (date: string | null) => formatDate(date),
    },
  ];

  const upcomingColumns = [
    {
      title: 'Schedule',
      dataIndex: 'scheduleName',
      key: 'scheduleName',
    },
    {
      title: 'Change Type',
      dataIndex: 'changeType',
      key: 'changeType',
      render: (type: string) => {
        const colors: Record<string, string> = {
          add: 'green',
          remove: 'red',
          modify: 'blue',
        };
        return (
          <Tag color={colors[type] || 'default'}>{type.toUpperCase()}</Tag>
        );
      },
    },
    {
      title: 'Effective Date',
      dataIndex: 'effectiveDate',
      key: 'effectiveDate',
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Days',
      dataIndex: 'days',
      key: 'days',
    },
  ];

  const groupedSchedules = groupSchedulesByProgramType();

  return (
    <div>
      <div style={{ marginBottom: 16, textAlign: 'right' }}>
        <Link to={`/admin/students/${studentId}/schedule-change`}>
          <Button type="primary" icon={<CalendarOutlined />}>
            Schedule Change
          </Button>
        </Link>
      </div>

      {Object.keys(groupedSchedules).length > 0 ? (
        Object.entries(groupedSchedules).map(([programType, typeSchedules]) => (
          <Card
            key={programType}
            title={
              <span>
                <CalendarOutlined style={{ marginRight: 8 }} />
                {programType} Schedules
              </span>
            }
            style={{ marginBottom: 16 }}
          >
            <Table
              dataSource={typeSchedules}
              columns={scheduleColumns}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </Card>
        ))
      ) : (
        <Card style={{ marginBottom: 16 }}>
          <Empty description="No current schedules" />
        </Card>
      )}

      {legacySchedules.length > 0 && (
        <>
          <Divider />
          <Card
            title={
              <span>
                <HistoryOutlined style={{ marginRight: 8 }} />
                Legacy Galaxy Schedules
              </span>
            }
            style={{ marginBottom: 16 }}
          >
            <Table
              dataSource={legacySchedules}
              columns={legacyColumns}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </Card>
        </>
      )}

      {upcomingChanges.length > 0 && (
        <>
          <Divider />
          <Card
            title={
              <span>
                <ClockCircleOutlined style={{ marginRight: 8 }} />
                Upcoming Schedule Changes
              </span>
            }
          >
            <Table
              dataSource={upcomingChanges}
              columns={upcomingColumns}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </Card>
        </>
      )}
    </div>
  );
};

export default ScheduleTab;
